import { useMemo } from 'react';
import type { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';

import { YES_NO_OPTIONS } from '@/contants/common';

import { getCustomerDefaultSettingsEffect } from '../settings/SettingsEffects';

interface CustomerDefaultSettings {
  customerKey: number; // example, 5060
  type: string; // example, Shipment
  parameterName: string; // example, DefaultCountryCode, UnitofMeasureQty, UnitofMeasureBillQty
  defaultValue: string; // example, US
}

export type CustomerDefaultSettingsResponse = CustomerDefaultSettings[];

const fetchCustomerDefaultSettings = async (
  customerKey: number,
  type?: string,
  parameterName?: string,
): Promise<CustomerDefaultSettingsResponse> => {
  const response: AxiosResponse<CustomerDefaultSettingsResponse> = await getCustomerDefaultSettingsEffect({
    customerKey,
    parameterName,
    type,
  });
  return response.data;
};

export const useGetDefaultCustomerSettings = (customerKey: number, type?: string, parameterName?: string) => {
  return useQuery<CustomerDefaultSettingsResponse, Error>({
    enabled: Boolean(customerKey),
    queryFn: () => fetchCustomerDefaultSettings(customerKey, type, parameterName),
    queryKey: ['customerDefaultSettings', customerKey, type, parameterName],
    // biome-ignore lint/style/noMagicNumbers: 5 seconds
    staleTime: 1000 * 5,
  });
};

const YES_VALUE = YES_NO_OPTIONS?.[0]?.value;

export const useCustomerDefaultFlags = (customerKey: number, type: string, parameterNames?: string[]) => {
  const { data, ...rest } = useGetDefaultCustomerSettings(customerKey, type);

  const flags = useMemo(() => {
    if (!data) {
      return {};
    }

    // If no parameterNames provided -> use all parameterName values
    const names = parameterNames?.length ? parameterNames : data.map((p) => p.parameterName);

    return names.reduce<Record<string, boolean>>((acc, name) => {
      const param = data.find((p) => p.parameterName === name);
      acc[name] = param?.defaultValue?.toUpperCase() === YES_VALUE;
      return acc;
    }, {});
  }, [data, parameterNames]);

  return {
    flags,
    ...rest,
  };
};
